import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Radio } from '@web/atoms';
import { useShopifyCart } from '@web/shopify';
import { Footer } from '../Footer';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { StyledForm } from '../StyledForm';
import { Question } from '../Question';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import { FieldWrapper } from './styles.js';
import { schema } from './schema';

export { schema } from './schema';

export const PupInfoForm = ({
  defaultValues,
  genderList,
  goBack,
  labelName,
  index,
  onSubmit,
  pupInfoQuestionCaption,
  pupInfoQuestionPrompt,
  ctaButtonText,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCart();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('pupname_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(index, 'dog_name', cart?.customer || customer),
    );
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };
  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer $maxWidth={391}>
        <Question
          caption={pupInfoQuestionCaption}
          prompt={pupInfoQuestionPrompt}
        >
          <Controller
            control={control}
            name="gender"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <>
                <Radio
                  {...field}
                  choices={genderList}
                  legendText={labelName}
                  name="gender"
                  error={error?.message || ''}
                  status={error?.message ? 'error' : null}
                />
              </>
            )}
          />
          <FieldWrapper>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  visuallyHideLabel
                  labelText={labelName}
                  error={fieldState.error?.message}
                  status={fieldState.error ? 'error' : null}
                  value={field.value || ''}
                />
              )}
            />
          </FieldWrapper>
        </Question>
      </FormFieldsContainer>

      <Footer
        mobileCTAText={ctaButtonText?.mobileCTA}
        desktopCTAText={ctaButtonText?.desktopCTA}
        onBackButtonClick={goBackWithReporter}
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
      />
    </StyledForm>
  );
};

PupInfoForm.displayName = 'PupInfoForm';

PupInfoForm.propTypes = {
  defaultValues: PropTypes.shape({
    gender: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  genderList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pupInfoQuestionCaption: PropTypes.string.isRequired,
  pupInfoQuestionPrompt: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.object,
};
